<script lang="ts">
  import { useForm } from '@inertiajs/svelte'
  import { onMount } from 'svelte'
  import FormRow from "./forms/_form_row.svelte"
  import FormErrors from "./forms/_form_errors.svelte"

  import {
    tenpo_reservation_path,
    tenpo_reservations_path
  } from '../../../javascript/routes.js'

  import { I18n } from "i18n-js"
  import i18n_translations from "../../../javascript/locales.json"

  let {
		RailsApp,
    reservation = {},
    merchant,
    packages = [],
    errors = {},
    tos_url = '',
    privacy_url = '',
    preselected_package_id = null,
  } = $props()

  const i18n = new I18n(i18n_translations)
  i18n.locale = RailsApp.I18n.locale
  i18n.defaultLocale = "en"
  i18n.enableFallback = true

  const initialDateValue = resolveInitialDateValue(normalizeDateInput((reservation as any)?.date))

  let selectedDateString = $state(extractDatePart(initialDateValue))
  let selectedSlot = $state(extractTimePart(initialDateValue))
  let availableSlots = $state<Array<{ label: string; value: string; disabled: boolean }>>(
    extractDatePart(initialDateValue) ? generateSlots(extractDatePart(initialDateValue)) : [],
  )
  let lastDateTime = $state(initialDateValue || '')
  let submitting = $state(false)
  let formEl = $state<HTMLFormElement | null>(null)
  let openDescriptions = $state<Record<string, boolean>>({})

  type ReservationSettings = {
    date?: {
      enable?: Array<number | string>
    }
    time?: {
      enable?: string
      disable?: string[]
    }
    seat_time?: {
      enable?: string
    }
    deadline?: Record<string, any>
    disable?: string[]
  }

  const supportsSeats = !!merchant?.reservation_enabled_for_seats
  const supportsPackages = !!merchant?.reservation_enabled_for_packages
  const supportsBoth = supportsSeats && supportsPackages
  const initialReservationType =
    (reservation as any)?.reservation_type ??
    (supportsBoth ? 'packages' : supportsPackages ? 'packages' : 'seats')

  const adultCountOptions = Array.from({ length: 50 }, (_, index) => index + 1)
  const childrenCountOptions = [0, ...adultCountOptions]
  const childAgeOptions = Array.from({ length: 18 }, (_, index) => index)

  const initialChildren = normalizeChildren((reservation as any)?.children)
  const initialNumberOfChildren = resolveNumberOfChildren((reservation as any)?.children)
  const initialNumberOfAdults = resolveInitialNumberOfAdults(
    (reservation as any)?.number_of_people,
    initialNumberOfChildren,
  )

  const form = useForm({
    date: initialDateValue,
    notes: (reservation as any)?.notes ?? '',
    name: (reservation as any)?.name ?? '',
    number_of_adults: initialNumberOfAdults,
    number_of_children: initialNumberOfChildren,
    children: initialChildren,
    telephone: (reservation as any)?.telephone ?? '',
    email: (reservation as any)?.email ?? '',
    remember_me: (reservation as any)?.remember_me ?? false,
    tos: false,
    package_reservations: packages.map((pkg) => ({
      id: pkg.reservation_package_id ?? null,
      package_id: pkg.package_id,
      units: pkg.units ?? 0,
    })),
    reservation_type: initialReservationType,
  })

  $effect(() => {
    if ($form.number_of_children === undefined || $form.number_of_children === null || $form.number_of_children === '') {
      $form.number_of_children = 0
    }
  })

  function cloneFormData(data: Record<string, any>) {
    return JSON.parse(JSON.stringify(data))
  }

  function isSameValue(a: any, b: any) {
    if (a === b) return true
    const aIsObject = a && typeof a === 'object'
    const bIsObject = b && typeof b === 'object'
    if (aIsObject || bIsObject) {
      return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
    }
    return false
  }

  let lastFormData = $state(cloneFormData($form.data()))

  // Server errors are keyed by model attribute, and a few form fields feed one
  // attribute between them, so clearing has to follow those edges. `base` holds
  // cross-field errors that no single field owns; any edit makes them stale.
  const errorKeyAliases: Record<string, string[]> = {
    number_of_adults: ['number_of_people'],
    number_of_children: ['number_of_people', 'children'],
    children: ['number_of_people'],
  }

  $effect(() => {
    const currentData = $form.data()
    const changedKeys = Object.keys(currentData).filter(
      (key) => !isSameValue(currentData[key], lastFormData[key]),
    )

    if (changedKeys.length > 0) {
      const affectedKeys = new Set<string>(['base'])
      for (const key of changedKeys) {
        affectedKeys.add(key)
        for (const alias of errorKeyAliases[key] ?? []) affectedKeys.add(alias)
      }

      const keysWithErrors = [...affectedKeys].filter((key) => $form.errors?.[key])
      if (keysWithErrors.length > 0) {
        $form.clearErrors(...keysWithErrors)
      }
      lastFormData = cloneFormData(currentData)
    }
  })

  // Initialize children ages array from the initial children input
  function parseChildrenAges(childrenInput: string | string[]): string[] {
    if (!childrenInput) return []
    if (Array.isArray(childrenInput)) {
      return childrenInput.map((age) => String(age).trim()).filter(Boolean)
    }
    return childrenInput.split(',').map(age => age.trim()).filter(Boolean)
  }

  let childrenAges = $state<string[]>(parseChildrenAges(initialChildren))

  // Sync childrenAges array size when number_of_children changes
  $effect(() => {
    const count = Number($form.number_of_children) || 0
    if (childrenAges.length < count) {
      // Add empty strings for new children
      childrenAges = [...childrenAges, ...Array(count - childrenAges.length).fill('')]
    } else if (childrenAges.length > count) {
      // Trim array if count decreased
      childrenAges = childrenAges.slice(0, count)
    }
  })

  // Update $form.children when childrenAges array changes
  $effect(() => {
    $form.children = [...childrenAges]
  })

  const createPath = tenpo_reservations_path({ locale: RailsApp.I18n.locale })
  const updatePath = (reservation as any)?.id ? tenpo_reservation_path((reservation as any).id, { locale: RailsApp.I18n.locale }) : ''
  const isNewRecord = (reservation as any)?.new_record ?? true

  const reservationEnabled =
    merchant?.reservation_enabled_for_packages ||
    merchant?.reservation_enabled_for_seats

  const tosUrl = tos_url
  const privacyUrl = privacy_url
  const introTitle = isNewRecord ? i18n.t('tenpo/reservations.new.title') : i18n.t('tenpo/reservations.edit.title')

  // コースごとに選べる数量の範囲（店舗管理画面の最低予約数 / 最高予約数。未設定は 1〜50）
  function unitBounds(index: number) {
    const pkg: any = packages[index] ?? {}
    const min = Math.max(1, Number(pkg.min_units) || 1)
    const max = Math.max(min, Number(pkg.max_units) || 50)
    return { min, max }
  }

  // 数量は 0（未選択）か、範囲内だけ。0 から + は最低数へ、最低数から − は 0 へ
  function setPackageUnits(index: number, units: number) {
    const { min, max } = unitBounds(index)
    const current = Number($form.package_reservations[index]?.units) || 0
    let next = Math.round(units)
    if (next <= 0) next = 0
    else if (next < min) next = next < current ? 0 : min
    else if (next > max) next = max
    $form.package_reservations[index].units = next
  }

  function toggleDescription(key: string) {
    openDescriptions = { ...openDescriptions, [key]: !openDescriptions[key] }
  }

  function normalizeChildren(children: any): string[] {
    if (Array.isArray(children)) {
      return children.map((value) => String(value).trim()).filter(Boolean)
    }
    if (children && typeof children === 'object') {
      return Object.values(children)
        .map((value) => String(value).trim())
        .filter(Boolean)
    }
    if (typeof children === 'string') {
      return children
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    }

    return []
  }

  function resolveNumberOfChildren(children: any): number {
    if (Array.isArray(children)) {
      return children.filter((value) => value !== null && value !== undefined && value !== '').length
    }
    if (typeof children === 'string') {
      return children
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean).length
    }
    return 0
  }

  // `number_of_people` is stored as the whole party. This form asks for adults
  // and children separately, so peel the children back off to seat the adults
  // select; handleSubmit adds them again on the way out.
  function resolveInitialNumberOfAdults(numberOfPeople: any, numberOfChildren: number): number {
    const parsed = Number(numberOfPeople)
    if (!Number.isFinite(parsed)) return 1
    const adults = parsed - numberOfChildren
    return adults < 1 ? 1 : adults
  }

  function normalizeDateInput(value: any): string {
    if (value instanceof Date) return formatDateTime(value)
    if (typeof value !== 'string') return ''
    const cleaned = value.replace('T', ' ')

    if (cleaned.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/)) {
      return cleaned.slice(0, 16)
    }

    return ''
  }

  function extractDatePart(value?: string) {
    if (!value) return ''
    const cleaned = value.replace('T', ' ')
    const match = cleaned.match(/^(\d{4}-\d{2}-\d{2})/)
    return match ? match[1] : ''
  }

  function extractTimePart(value?: string) {
    if (!value) return ''
    const cleaned = value.replace('T', ' ')
    const match = cleaned.match(/\d{4}-\d{2}-\d{2}\s+(\d{2}:\d{2})/)
    return match ? match[1] : ''
  }

  function formatDateTime(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    return `${year}-${month}-${day} ${hours}:${minutes}`
  }

  function formatDateOnly(date: Date, timezoneOffsetHours?: number): string {
    const targetDate = new Date(date)

    if (timezoneOffsetHours !== undefined) {
      const utcTime = targetDate.getTime() + targetDate.getTimezoneOffset() * 60000
      targetDate.setTime(utcTime + timezoneOffsetHours * 3600000)
    }

    const year = targetDate.getFullYear()
    const month = String(targetDate.getMonth() + 1).padStart(2, '0')
    const day = String(targetDate.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  function escapeHtml(input: string): string {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }
    return input.replace(/[&<>"']/g, (char) => map[char])
  }

  function simpleFormat(input: string | undefined | null): string {
    if (!input) return ''
    const escaped = escapeHtml(String(input))
    const paragraphs = escaped.replace(/\r\n?/g, '\n').split(/\n{2,}/)
    return paragraphs
      .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br />')}</p>`)
      .join('\n')
  }

  function parseTimeRanges(value?: string) {
    if (typeof value !== 'string' || !value.trim()) return []

    return value
      .split(/\r?\n/)
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((range) => {
        const [start, end] = range.split('-').map((part) => part?.trim())
        if (!start || !end) return null

        const [startHour, startMinute] = start.split(':').map((value) => Number(value))
        const [endHour, endMinute] = end.split(':').map((value) => Number(value))

        if (
          !Number.isInteger(startHour) ||
          !Number.isInteger(startMinute) ||
          !Number.isInteger(endHour) ||
          !Number.isInteger(endMinute)
        ) {
          return null
        }

        return {
          startMinutes: startHour * 60 + startMinute,
          endMinutes: endHour * 60 + endMinute,
        }
      })
      .filter(Boolean) as Array<{ startMinutes: number; endMinutes: number }>
  }

  function parseDisabledDateTimeRanges(reservationSettings?: ReservationSettings) {
    const entries = Array.isArray(reservationSettings?.disable)
      ? reservationSettings?.disable
      : Array.isArray(reservationSettings?.time?.disable)
        ? reservationSettings?.time?.disable
        : []

    return entries
      .map((entry) => {
        if (typeof entry !== 'string') return null

        const cleaned = entry.trim()
        if (!cleaned) return null

        const [datePart, timePart] = cleaned.split(' ')
        if (!datePart || !timePart) return null

        const [startTime, endTime] = timePart.split('-')
        if (!startTime || !endTime) return null

        const createDate = (time: string) => {
          const [hour, minute] = time.split(':').map((value) => Number(value))
          if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null

          const [year, month, day] = datePart.split('-').map((value) => Number(value))
          if (
            !Number.isInteger(year) ||
            !Number.isInteger(month) ||
            !Number.isInteger(day)
          ) {
            return null
          }

          return new Date(year, month - 1, day, hour, minute)
        }

        const startDate = createDate(startTime)
        const endDate = createDate(endTime)

        if (!startDate || !endDate) return null

        return { startDate, endDate }
      })
      .filter(Boolean) as Array<{ startDate: Date; endDate: Date }>
  }

  function normalizeEnabledDays(enabledDays?: Array<number | string>) {
    const normalized = (enabledDays || [])
      .map((day: any) => Number(day))
      .filter((day: number) => Number.isInteger(day))

    const weekdays = normalized
      .filter((day) => day >= 1 && day <= 7)
      .map((day) => (day === 7 ? 0 : day))

    // 2026-10-29：予約締切がある店は、当日の可否を締切で決める（ここでは当日を閉じない）
    const includesToday = normalized.includes(0) || !!(merchant as any)?.reservation_settings?.deadline

    return { weekdays, includesToday }
  }

  function isDateEnabled(date: Date, normalizedEnabled: { weekdays: number[]; includesToday: boolean }) {
    if (!normalizedEnabled.weekdays.length) return true

    const dayOfWeek = date.getDay()
    if (!normalizedEnabled.weekdays.includes(dayOfWeek)) {
      return false
    }

    if (!normalizedEnabled.includesToday && formatDateOnly(date) === formatDateOnly(new Date(), 9)) {
      return false
    }

    return true
  }

  // earliest selectable date (today in JST, the merchant timezone)
  const minDateString = formatDateOnly(new Date(), 9)

  function isSlotAvailable(time: string) {
    return !!time && availableSlots.some((slot) => slot.value === time && !slot.disabled)
  }

  // 2026-10-29：席予約は「席予約可能な時間」、コース予約は「予約注文可能な時間」（どちらも「開始〜最終受付」）
  //   フォームができる前（最初の日付・時間を決めるとき）は、最初に選ばれる種類と同じ決め方
  function slotTimeSource(settings?: ReservationSettings, reservationType?: string) {
    const type =
      reservationType ??
      (reservation as any)?.reservation_type ??
      (merchant?.reservation_enabled_for_packages ? 'packages' : 'seats')
    const seatTime = settings?.seat_time?.enable
    if (type === 'seats' && typeof seatTime === 'string') return seatTime
    return settings?.time?.enable
  }

  // 2026-10-29：予約締切（席／コース別）。'lead' = 来店の N 分前まで、'cutoff' = 来店日の N 日前の HH:MM まで。無い店は今までどおり N 分前
  function slotDeadline(settings?: ReservationSettings, reservationType?: string) {
    const type =
      reservationType ??
      (reservation as any)?.reservation_type ??
      (merchant?.reservation_enabled_for_packages ? 'packages' : 'seats')
    const d = settings?.deadline?.[type === 'seats' ? 'seat' : 'order']
    return d && (d.mode === 'lead' || d.mode === 'cutoff') ? d : null
  }

  function deadlineUtc(slotUtc: number, year: number, month: number, day: number, d: any, bufferMinutes: number, jstOffsetMs: number) {
    if (d?.mode === 'cutoff') {
      const [h, mi] = String(d.time || '23:59').split(':').map((value) => Number(value))
      return Date.UTC(year, month - 1, day - Number(d.days || 0), h || 0, mi || 0, 0, 0) - jstOffsetMs
    }
    const minutes = d?.mode === 'lead' ? Number(d.minutes || 0) : bufferMinutes
    return slotUtc - minutes * 60 * 1000
  }

  function generateSlots(dateStr: string, reservationType?: string) {
    if (!dateStr) return []

    const settings = merchant?.reservation_settings

    // Respect the merchant's bookable weekdays (and same-day rule): a date on a
    // closed day yields no slots, mirroring the disabled days in the old picker.
    const [y, m, d] = dateStr.split('-').map(Number)
    if (!isDateEnabled(new Date(y, m - 1, d), normalizeEnabledDays(settings?.date?.enable))) {
      return []
    }

    const allowedRanges = parseTimeRanges(slotTimeSource(settings, reservationType))
    const disabledRanges = parseDisabledDateTimeRanges(settings)

    // Buffer time in minutes (default 60 if not set)
    const bufferMinutes = merchant?.minimum_advance_reservation_time ?? 60
    const deadline = slotDeadline(settings, reservationType)

    // Calculate the earliest allowed UTC timestamp
    // The server interprets submitted times as JST (UTC+9)
    const earliestAllowedUtc = Date.now() + bufferMinutes * 60 * 1000

    // Parse the selected date
    const [year, month, day] = dateStr.split('-').map(Number)

    // JST offset in milliseconds (UTC+9)
    const jstOffsetMs = 9 * 60 * 60 * 1000

    const slots: Array<{ label: string; value: string; disabled: boolean }> = []

    allowedRanges.forEach((range) => {
      // 終了 = 最終受付（2026-10-29）。最終受付の時刻も出す
      for (let minutes = range.startMinutes; minutes <= range.endMinutes; minutes += 30) {
        const hour = Math.floor(minutes / 60)
        const minute = minutes % 60
        const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`

        // Calculate slot's UTC timestamp
        // The date/time string represents JST, so we convert to UTC by subtracting 9 hours
        const slotUtcTimestamp = Date.UTC(year, month - 1, day, hour, minute, 0, 0) - jstOffsetMs

        const slotStart = new Date(`${dateStr}T${value}:00`)
        const slotEnd = new Date(slotStart.getTime() + 30 * 60 * 1000)

        // Check if slot is in a disabled date-time range
        const isInDisabledRange = disabledRanges.some(
          ({ startDate, endDate }) => slotStart < endDate && slotEnd > startDate,
        )

        // Check if slot is in the past or within buffer time
        // 2026-10-29：過去か、予約締切を過ぎているか
        const nowUtc = Date.now()
        const isPastOrTooClose =
          slotUtcTimestamp < nowUtc ||
          nowUtc > deadlineUtc(slotUtcTimestamp, year, month, day, deadline, bufferMinutes, jstOffsetMs)

        const isDisabled = isInDisabledRange || isPastOrTooClose

        slots.push({ label: value, value, disabled: isDisabled })
      }
    })

    return slots
  }

  function formatDateForSubmit(value: string): string {
    if (!value) return ''
    return value.replace('T', ' ').trim()
  }

  function pickMiddleAvailableSlot(slots: Array<{ label: string; value: string; disabled: boolean }>) {
    const enabledSlots = slots.filter((slot) => !slot.disabled)
    if (!enabledSlots.length) return null
    const middleIndex = Math.floor((enabledSlots.length - 1) / 2)
    return enabledSlots[middleIndex]
  }

  function resolveInitialDateValue(currentValue?: string) {
    if (currentValue) return currentValue

    const targetDateString = findFirstAvailableDateString(2)
    if (!targetDateString) return ''

    const slots = generateSlots(targetDateString)
    const middleSlot = pickMiddleAvailableSlot(slots)
    if (!middleSlot) return targetDateString

    return `${targetDateString} ${middleSlot.value}`
  }

  function findFirstAvailableDateString(minDaysFromNow: number, maxLookaheadDays = 365) {
    const settings = merchant?.reservation_settings
    const normalizedEnabled = normalizeEnabledDays(settings?.date?.enable)

    for (let offset = minDaysFromNow; offset <= minDaysFromNow + maxLookaheadDays; offset += 1) {
      const candidateDate = new Date()
      candidateDate.setDate(candidateDate.getDate() + offset)

      if (!isDateEnabled(candidateDate, normalizedEnabled)) continue

      const dateString = formatDateOnly(candidateDate)
      const slots = generateSlots(dateString)
      if (slots.some((slot) => !slot.disabled)) {
        return dateString
      }
    }

    const fallbackDate = new Date()
    fallbackDate.setDate(fallbackDate.getDate() + minDaysFromNow)
    return formatDateOnly(fallbackDate)
  }

  function syncFormDate() {
    const dateTime = selectedDateString
      ? (selectedSlot ? `${selectedDateString} ${selectedSlot}` : selectedDateString)
      : ''
    if (dateTime !== lastDateTime) {
      lastDateTime = dateTime
      $form.date = dateTime
    }
  }

  // Recompute the available slots for the chosen day and default to a sensible
  // time. Used on date change and when the reservation type changes.
  function updateSlotsAndForm() {
    if (!selectedDateString) {
      availableSlots = []
      selectedSlot = ''
      syncFormDate()
      return
    }

    availableSlots = generateSlots(selectedDateString, $form.reservation_type)

    if (selectedSlot && !isSlotAvailable(selectedSlot)) {
      selectedSlot = ''
    }

    if (!selectedSlot) {
      const middleAvailable = pickMiddleAvailableSlot(availableSlots)
      if (middleAvailable) {
        selectedSlot = middleAvailable.value
      }
    }

    syncFormDate()
  }

  function handleDateChange(value: string) {
    selectedDateString = value
    selectedSlot = ''
    updateSlotsAndForm()
  }

  // Honour whatever time the user picked in the native time input; availability
  // is reflected in the message + submit button rather than by overriding it.
  function handleTimeChange(value: string) {
    selectedSlot = value
    if (selectedDateString) {
      availableSlots = generateSlots(selectedDateString, $form.reservation_type)
    }
    syncFormDate()
  }

  function formatPrice(price?: number) {
    return i18n.numberToCurrency(price, {unit: '¥', precision: 0, format: '%u%n'})
  }

  function updatePackageUnits(index: number, units: number) {
    $form.package_reservations[index].units = units
  }

  function handleSubmit(event: Event) {
    event.preventDefault()

    const selectedReservationType = $form.reservation_type === 'packages' ? 'packages' : 'seats'
    const packageReservationsForSubmit =
      selectedReservationType === 'packages'
      ? $form.package_reservations.filter((entry) => entry?.package_id && Number(entry.units) > 0)
      : []

    const childrenForSubmit = Array.isArray($form.children) ? [...$form.children] : []
    const numberOfAdults = Number($form.number_of_adults) || 1

    const submitData = {
      reservation: {
        date: $form.date,
        notes: $form.notes,
        name: $form.name,
        number_of_people: numberOfAdults + childrenForSubmit.length,
        children: childrenForSubmit,
        telephone: $form.telephone,
        email: $form.email,
        remember_me: !!$form.remember_me,
        tos: !!$form.tos,
        package_reservations_attributes: packageReservationsForSubmit,
      },
      reservation_type: $form.reservation_type,
    }

    // A successful create/update answers 409 + X-Inertia-Location, so Inertia
    // hands the browser a full page load. It still fires onFinish though, which
    // resets $form.processing — leaving the button idle and clickable for the
    // whole document load. Track the busy state ourselves and only release it on
    // the paths that keep us on this page.
    submitting = true
    const options = {
      preserveScroll: false,
      onSuccess: () => { submitting = false },
      onError: () => { submitting = false },
      onCancel: () => { submitting = false },
    }

    if (!isNewRecord) {
      $form.transform(() => submitData).patch(updatePath, options)
    } else {
      $form.transform(() => submitData).post(createPath, options)
    }
  }

  onMount(() => {
    // make sure the initial slot list + submitted value are consistent
    updateSlotsAndForm()

    // 送信 → 予約ページ → 「戻る」でブラウザが送信中の画面をそのまま復元すると（bfcache）、
    // submitting が true のまま戻ってくるのでボタンとバーが回り続ける。復元時に戻す
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) submitting = false
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  })

  const errorMessages = $derived(
    Object.values($form.errors || {})
      .flat()
      .filter(Boolean),
  )
  const hasErrors = $derived(errorMessages.length > 0)
  const showPackageReservations = $derived(supportsPackages && $form.reservation_type === 'packages')
  const packageTableDisabled = $derived(!showPackageReservations)

  const selectedPackages = $derived(
    $form.package_reservations
      .map((pr, index) => ({
        ...packages[index],
        units: pr.units,
        subtotal: pr.units * (packages[index]?.price ?? 0),
      }))
      .filter((pkg) => pkg.units > 0)
  )
  const totalPackagePrice = $derived(
    selectedPackages.reduce((sum, pkg) => sum + pkg.subtotal, 0)
  )
  const hasSelectedPackages = $derived(selectedPackages.length > 0)

  // order-summary sidebar
  const summaryMeta = $derived.by(() => {
    const parts: string[] = []
    if (selectedDateString) parts.push(selectedDateString)
    if (selectedSlot) parts.push(selectedSlot)
    const partySize = (Number($form.number_of_adults) || 0) + childrenAges.length
    if (partySize) {
      parts.push(`${partySize} ${i18n.t('tenpo/reservations.show.attributes.number_of_people_options')}`)
    }
    return parts.join(' · ')
  })
  const summaryImage = $derived((merchant as any)?.cover?._1x || '')

  const requiresPackages = $derived($form.reservation_type === 'packages')
  const hasValidPackageSelection = $derived(!requiresPackages || hasSelectedPackages)
  const requiresSlot = $derived($form.reservation_type === 'seats')
  const hasValidSlotSelection = $derived(
    !requiresSlot ||
      (!!selectedDateString &&
        !!selectedSlot &&
        availableSlots.some((slot) => !slot.disabled && slot.value === selectedSlot)),
  )
  const isSubmitting = $derived($form.processing || submitting)
  const formSubmitDisabled = $derived(
    isSubmitting ||
    !hasValidSlotSelection ||
    !hasValidPackageSelection,
  )

  // 固定バーの予約ボタン：未入力の必須項目があればそこへ移動、揃っていれば送信
  function focusFirstMissingField(): boolean {
    const checks: Array<[boolean, string]> = [
      [requiresPackages && !hasSelectedPackages, 'reservation_packages'],
      [!$form.date || (requiresSlot && !hasValidSlotSelection), 'reservation_date'],
      [!String($form.name ?? '').trim(), 'reservation_name'],
      [!String($form.telephone ?? '').trim(), 'reservation_telephone'],
      [!String($form.email ?? '').trim(), 'reservation_email'],
      [!$form.tos, 'reservation_tos'],
    ]
    const missing = checks.find(([isMissing]) => isMissing)
    if (!missing) return false
    const el = document.getElementById(missing[1])
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      window.setTimeout(() => (el as HTMLElement).focus?.({ preventScroll: true }), 350)
    }
    return true
  }

  function handleStickySubmit() {
    if (isSubmitting) return
    if (focusFirstMissingField()) return
    formEl?.requestSubmit()
  }
</script>

<svelte:head>
  <title>
    {merchant.name} - {i18n.t('tenpo/reservations.new.title')}
  </title>
</svelte:head>

<div class="Tenpo_Reservations New" data-turbolinks="false">
  <div class="merchant-destination py-4 py-lg-5">
    {#if reservationEnabled !== false}
      <FormErrors locale={RailsApp.I18n.locale} messages={errorMessages} />

      <div class="reservation-split">
        <main class="reservation-main">
          <section class="content-card border-top-0">
            <div class="content-card-body">
              <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-3 mb-4">
                <div>
                  <h2 class="h3 mb-0">{introTitle}</h2>
                </div>
              </div>

              <form bind:this={formEl} on:submit|preventDefault={handleSubmit}>
            {#if supportsBoth && isNewRecord}
              <FormRow>
                {#snippet key()}
                <label class="col-form-label p-0" for="translation_source_file">
                  <div class="fw-semibold">{i18n.t('tenpo/reservations.new.sections.form.reservation_type.title')}</div>
                  <p class="small mb-0 mt-2">{i18n.t('tenpo/reservations.new.sections.form.reservation_type.description')}</p>
                </label>
                {/snippet}
                {#snippet value()}
                <div class="d-grid gap-2">
                    <input
                      class="btn-check"
                      type="radio"
                      name="reservation_type"
                      id="reservation_type_seats"
                      value="seats"
                      bind:group={$form.reservation_type}
                      on:change={() => updateSlotsAndForm()}
                    />
                    <label class="btn btn-outline-dark text-start p-3" for="reservation_type_seats">
                      <strong class="d-block">{i18n.t('tenpo/reservations.new.sections.form.reservation_type.seats_title')}</strong>
                      <span class="small">{i18n.t('tenpo/reservations.new.sections.form.reservation_type.seats_description')}</span>
                    </label>
                    <input
                      class="btn-check"
                      type="radio"
                      name="reservation_type"
                      id="reservation_type_packages"
                      value="packages"
                      bind:group={$form.reservation_type}
                      on:change={() => updateSlotsAndForm()}
                    />
                    <label class="btn btn-outline-dark text-start p-3" for="reservation_type_packages">
                      <strong class="d-block">{i18n.t('tenpo/reservations.new.sections.form.reservation_type.packages_title')}</strong>
                      <span class="small">{i18n.t('tenpo/reservations.new.sections.form.reservation_type.packages_description')}</span>
                    </label>
                </div>
                {#if $form.errors.reservation_type}<div class="form-text text-danger fw-bold">{$form.errors.reservation_type}</div>{/if}
                {/snippet}
              </FormRow>
            {/if}

            {#if supportsPackages && $form.reservation_type == "packages"}
              <div class="py-4" id="reservation_packages">
                <label class="col-form-label p-0 mb-3">
                  <div class="fw-semibold">{i18n.t('activerecord.attributes.reservation.packages')}</div>
                  <p class="small mb-0 mt-2">{i18n.t('tenpo/reservations.new.sections.form.packages_help')}</p>
                </label>
                {#if packages.length}
                  <fieldset class="border-0 p-0 m-0" aria-hidden={packageTableDisabled} disabled={packageTableDisabled}>
                    <div class="pkg-rows">
                      {#each packages as pkg, index (pkg.package_id)}
                        {@const units = Number($form.package_reservations[index]?.units) || 0}
                        {@const bounds = unitBounds(index)}
                        {@const descKey = String(pkg.package_id)}
                        <div class="pkg-row" class:is-selected={units > 0}>
                          <div class="pkg-row-main">
                            <div class="pkg-row-name">{pkg.name}</div>
                            <div class="pkg-row-meta">
                              <span class="pkg-row-price">{formatPrice(pkg.price)} / {i18n.t('tenpo/reservations.new.sections.form.package_unit_label')}</span>
                              {#if bounds.min > 1 || bounds.max < 50}
                                <span class="pkg-row-range small text-muted">{i18n.t('tenpo/reservations.new.sections.form.package_unit_range', { min: bounds.min, max: bounds.max })}</span>
                              {/if}
                              {#if pkg.description}
                                <button
                                  type="button"
                                  class="pkg-row-toggle"
                                  aria-expanded={!!openDescriptions[descKey]}
                                  on:click={() => toggleDescription(descKey)}
                                >
                                  {i18n.t('tenpo/reservations.new.sections.form.package_description_toggle')}
                                  <span aria-hidden="true">{openDescriptions[descKey] ? '▴' : '▾'}</span>
                                </button>
                              {/if}
                            </div>
                            {#if pkg.description && openDescriptions[descKey]}
                              <div class="pkg-row-desc">{@html simpleFormat(pkg.description)}</div>
                            {/if}
                          </div>
                          <div class="pkg-stepper" role="group" aria-label={pkg.name}>
                            <button
                              type="button"
                              class="pkg-stepper-btn"
                              aria-label={i18n.t('tenpo/reservations.new.sections.form.package_decrease')}
                              disabled={units <= 0}
                              on:click={() => setPackageUnits(index, units - 1)}
                            >−</button>
                            <span class="pkg-stepper-value" aria-live="polite">{units}</span>
                            <button
                              type="button"
                              class="pkg-stepper-btn"
                              aria-label={i18n.t('tenpo/reservations.new.sections.form.package_increase')}
                              disabled={units >= bounds.max}
                              on:click={() => setPackageUnits(index, units + 1)}
                            >+</button>
                          </div>
                        </div>
                      {/each}
                    </div>
                  </fieldset>
                {/if}
              </div>
            {/if}

            <FormRow>
              {#snippet key()}
                <label class="col-form-label p-0" for="translation_source_file">
                  <div class="fw-semibold">{i18n.t('activerecord.attributes.reservation.date')}</div>
                  <p class="small mb-0 mt-2">{i18n.t('tenpo/reservations.new.sections.form.date_help')}</p>
                </label>
              {/snippet}
              {#snippet value()}
              <div class="d-flex flex-column flex-sm-row gap-3" style="max-width: 36rem;">
                <div class="flex-fill">
                  <label class="form-label small text-muted" for="reservation_date">{i18n.t('tenpo/reservations.new.datetime.date_label')}</label>
                  <input
                    id="reservation_date"
                    type="date"
                    class="form-control"
                    min={minDateString}
                    bind:value={selectedDateString}
                    autocomplete="off"
                    on:change={(event) => handleDateChange((event.target as HTMLInputElement).value)}
                  />
                </div>
                <div class="flex-fill">
                  <label class="form-label small text-muted" for="reservation_time">{i18n.t('tenpo/reservations.new.datetime.time_label')}</label>
                  <select
                    id="reservation_time"
                    class="form-select"
                    bind:value={selectedSlot}
                    disabled={!selectedDateString || availableSlots.length === 0}
                    on:change={(event) => handleTimeChange((event.target as HTMLSelectElement).value)}
                  >
                    <option value="" disabled>{i18n.t('tenpo/reservations.new.datetime.time_label')}</option>
                    {#each availableSlots as slot}
                      <option value={slot.value} disabled={slot.disabled}>{slot.label}</option>
                    {/each}
                  </select>
                </div>
              </div>

              {#if selectedDateString && availableSlots.length === 0}
                <div class="mt-3 form-text text-danger">{i18n.t('tenpo/reservations.new.datetime.no_slots')}</div>
              {:else if selectedDateString && selectedSlot && !isSlotAvailable(selectedSlot)}
                <div class="mt-3 form-text text-danger">{i18n.t('tenpo/reservations.new.datetime.unavailable')}</div>
              {/if}
              {#if $form.errors.date}<div class="mt-2 form-text text-danger fw-bold">{$form.errors.date}</div>{/if}
              {/snippet}
            </FormRow>

            <FormRow>
              {#snippet key()}
              <label class="col-form-label p-0" for="reservation_name">
                <div class="fw-semibold">{i18n.t('tenpo/reservations.new.sections.form.contact')}</div>
                <p class="small mb-0 mt-2">{i18n.t('tenpo/reservations.new.sections.form.contact_help')}</p>
              </label>
              {/snippet}
              {#snippet value()}
              <div class="d-grid gap-3" style="max-width: 36rem;">
                <div>
                  <input
                    id="reservation_name"
                    type="text"
                    class="form-control"
                    bind:value={$form.name}
                    placeholder="{i18n.t('activerecord.attributes.reservation.name')}"
                  />
                  {#if $form.errors.name}<div class="form-text text-danger fw-bold">{$form.errors.name}</div>{/if}
                </div>
                <div>
                  <input
                    id="reservation_telephone"
                    type="text"
                    class="form-control"
                    bind:value={$form.telephone}
                    placeholder="{i18n.t('activerecord.attributes.reservation.telephone')}"
                  />
                  {#if $form.errors.telephone}<div class="form-text text-danger fw-bold">{$form.errors.telephone}</div>{/if}
                </div>
                <div>
                  <input
                    id="reservation_email"
                    type="email"
                    class="form-control"
                    bind:value={$form.email}
                    placeholder="{i18n.t('activerecord.attributes.reservation.email')}"
                  />
                  {#if $form.errors.email}<div class="form-text text-danger fw-bold">{$form.errors.email}</div>{/if}
                </div>
              </div>
              {/snippet}
            </FormRow>

            <FormRow>
              {#snippet key()}
              <label class="col-form-label p-0">
                <div class="fw-semibold">{i18n.t('activerecord.attributes.reservation.number_of_people')}</div>
                <p class="small mb-0 mt-2">{i18n.t('tenpo/reservations.new.sections.form.people_help')}</p>
              </label>
              {/snippet}
              {#snippet value()}
              <div class="d-flex flex-column flex-sm-row gap-3 align-items-start" style="max-width: 36rem;">
                <div class="input-group" style="width: fit-content;">
                  <select
                    class="form-select"
                    style="width: 5rem;"
                    bind:value={$form.number_of_adults}
                  >
                    {#each adultCountOptions as option}
                      <option value={option}>{option}</option>
                    {/each}
                  </select>
                  <span class="input-group-text">
                    {i18n.t('tenpo/reservations.show.attributes.adults')}
                  </span>
                </div>
                {#if $form.errors.number_of_people}<div class="form-text text-danger fw-bold">{$form.errors.number_of_people}</div>{/if}
                <div>
                  <div class="input-group" style="width: fit-content;">
                    <select
                      class="form-select"
                      style="width: 5rem;"
                      bind:value={$form.number_of_children}
                    >
                      {#each childrenCountOptions as option}
                        <option value={option}>{option}</option>
                      {/each}
                    </select>
                    <span class="input-group-text">
                      {i18n.t('tenpo/reservations.show.attributes.children')}
                    </span>
                  </div>
                  {#if childrenAges.length > 0}
                    <div class="form-text mt-2">{i18n.t('tenpo/reservations.show.attributes.children_ages_prompt')}</div>
                    {#each childrenAges as _, index}
                      <div class="input-group mt-2" style="width: fit-content;">
                        <select
                          id="reservation_children_{index}"
                          class="form-select"
                          style="width: 5rem;"
                          bind:value={childrenAges[index]}
                        >
                          {#each childAgeOptions as option}
                            <option value={option}>{option}</option>
                          {/each}
                        </select>
                        <span class="input-group-text">
                          {i18n.t('tenpo/reservations.show.attributes.years_old')}
                        </span>
                      </div>
                    {/each}
                  {/if}
                  {#if $form.errors.children}<div class="mt-2 form-text text-danger fw-bold">{$form.errors.children}</div>{/if}
                </div>
              </div>
              {/snippet}
            </FormRow>

            <FormRow>
              {#snippet key()}
              <label class="col-form-label p-0" for="reservation_notes">
                <div class="fw-semibold">{i18n.t('activerecord.attributes.reservation.notes')}</div>
                <p class="small mb-0 mt-2">{i18n.t('tenpo/reservations.new.sections.form.notes_help')}</p>
              </label>
              {/snippet}
              {#snippet value()}
              <div style="max-width: 36rem;">
                <textarea
                  id="reservation_notes"
                  class="form-control"
                  rows="4"
                  bind:value={$form.notes}
                ></textarea>
                {#if $form.errors.notes}<div class="form-text text-danger fw-bold">{$form.errors.notes}</div>{/if}
              </div>
              {/snippet}
            </FormRow>

            <FormRow>
              {#snippet key()}
              <div></div>
              {/snippet}
              {#snippet value()}
              <div style="max-width: 36rem;">
                  <div class="form-check remember-me">
                    <input
                      id="reservation_remember_me"
                      type="checkbox"
                      class="form-check-input"
                      bind:checked={$form.remember_me}
                    />
                    <label class="form-check-label" for="reservation_remember_me">
                      {i18n.t('activerecord.attributes.reservation.remember_me')}
                    </label>
                  </div>
                  <div class="form-check mt-3">
                    <input
                      id="reservation_tos"
                      type="checkbox"
                      class="form-check-input"
                      bind:checked={$form.tos}
                    />
                    <label class="form-check-label" for="reservation_tos">
                      {@html i18n.t('tenpo/reservations.new.tos', {
                        tos: `<a href="${tosUrl}" target="_blank" rel="noopener noreferrer">${i18n.t('homepage/page.tos.title')}</a>`,
                        privacy: `<a href="${privacyUrl}" target="_blank" rel="noopener noreferrer">${i18n.t('homepage/page.privacy.title')}</a>`,
                        })}
                    </label>
                  </div>
              </div>
              {#if $form.errors.tos}<div class="form-text text-danger fw-bold">{$form.errors.tos}</div>{/if}
              <button
                class="btn btn-dark btn-lg mt-3 px-4 d-inline-flex align-items-center gap-2"
                type="submit"
                disabled={formSubmitDisabled}
                aria-busy={isSubmitting}
              >
                {#if isSubmitting}
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span>{i18n.t('form_actions.in_progress')}</span>
                {:else}
                  <span>
                    {$form.reservation_type == 'packages'
                      ? i18n.t('tenpo/reservations.new.cta.pay_and_reserve')
                      : i18n.t('tenpo/reservations.new.cta.reserve')}
                  </span>
                {/if}
              </button>
              {/snippet}
            </FormRow>
            <div class="reservation-sticky-bar d-lg-none">
              <div class="reservation-sticky-summary">
                {#if requiresPackages}
                  <span class="reservation-sticky-label">{i18n.t('tenpo/reservations.new.sections.form.total')}</span>
                  <span class="reservation-sticky-amount">{formatPrice(totalPackagePrice)}</span>
                {:else}
                  <span class="reservation-sticky-amount">{summaryMeta}</span>
                {/if}
              </div>
              <button type="button" class="btn btn-dark reservation-sticky-btn" disabled={isSubmitting} on:click={handleStickySubmit}>
                {#if isSubmitting}
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                {:else}
                  {$form.reservation_type == 'packages'
                    ? i18n.t('tenpo/reservations.new.cta.pay_and_reserve')
                    : i18n.t('tenpo/reservations.new.cta.reserve')}
                {/if}
              </button>
            </div>
          </form>
            </div>
          </section>
        </main>

        <aside class="reservation-aside d-none d-lg-block">
          <div class="reservation-summary">
            <div class="rs-head">
              <h3>{i18n.t('tenpo/reservations.new.sections.form.order_overview')}</h3>
            </div>
            <div class="rs-body">
              <div class="rs-item">
                {#if summaryImage}
                  <img src={summaryImage} alt={merchant.name} />
                {:else}
                  <div class="rs-item-ph"></div>
                {/if}
                <div>
                  <div class="rs-nm">{merchant.name}</div>
                  {#if summaryMeta}<div class="rs-sub mt-1">{summaryMeta}</div>{/if}
                </div>
              </div>

              {#if hasSelectedPackages}
                <div class="rs-section">
                  <div class="rs-lines">
                    {#each selectedPackages as pkg}
                      <div class="rs-line">
                        <div class="rs-line-name">{pkg.name}</div>
                        <div class="rs-line-calc">
                          <span>{formatPrice(pkg.price)} × {pkg.units}</span>
                          <span class="rs-line-sub">{formatPrice(pkg.subtotal)}</span>
                        </div>
                      </div>
                    {/each}
                  </div>
                  <div class="rs-row rs-total">
                    <span>{i18n.t('tenpo/reservations.new.sections.form.total')}</span>
                    <span class="rs-amt">{formatPrice(totalPackagePrice)}</span>
                  </div>
                </div>
              {:else if requiresPackages}
                <div class="rs-section">
                  <div class="rs-row muted">
                    <span>{i18n.t('tenpo/reservations.new.sections.form.packages_help')}</span>
                  </div>
                </div>
              {/if}
            </div>
          </div>
        </aside>
      </div>
    {:else}
      <p>{i18n.t('tenpo/reservations.new.not_accept_reservation')}</p>
    {/if}
  </div>
</div>
